import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ChevronDownIcon } from 'lucide-react';
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
  DialogDescription,
  Form,
  FormField,
  Button,
  MobileNavBar,
  useZodForm,
  useModalControls,
} from '@noor/ui';
import { toast, useFormSeed, type ModalRenderProps } from '@noor/lib';
import { getApiErrorMessage, getApiFieldErrors } from '@noor/api';
import { cn } from '@noor/lib/cn';
import {
  ASSIGNABLE_ROLES,
  formatUserPhone,
  roleLabelKey,
  useUser,
} from '@/entities/user';
import {
  buildCreateUserFormSchema,
  buildEditUserFormSchema,
  type CreateUserFormValues,
  type EditUserFormValues,
} from '../model/schema';
import { useCreateUser, useRenameUser } from '../model/use-upsert-user';
import { useRemoveUser } from '../model/use-remove-user';
import { toCreateUserFieldErrors } from '../lib/attach-errors';
import { MobileRolePicker } from './mobile-role-picker';

/** Figma's one input treatment: a 48px grey pill, 24px radius (26295:98784). */
const FIELD = 'h-12 rounded-3xl border-none bg-muted px-4 text-base shadow-none';

/** Figma label: 14px medium, gray-700, 4px inset. */
const LABEL =
  'flex items-center gap-px px-1 text-sm font-medium tracking-tight text-secondary-foreground';

/** Validation text that takes up no room until there is something to say. */
const MESSAGE = 'min-h-0 px-1 empty:hidden';

const FIELD_GROUP = 'gap-2';

type UserFormSheetMobileProps =
  | ModalRenderProps<'add-user'>
  | ModalRenderProps<'edit-user'>;

/**
 * Add / edit employee on mobile (Figma 26295:98779 add, 26295:98930 edit) — a
 * full-screen native-app takeover, not the desktop's centered dialog rendered
 * narrow.
 *
 * Rendered through the bare `Dialog` primitives rather than the shared `Modal`
 * shell: that shell centers a card with a close-X over a backdrop, while this
 * screen IS the surface — full-bleed, its own back arrow, no close-X — the
 * same treatment `branch-sheet.mobile.tsx` uses.
 *
 * **Two fields the design draws are absent, and both are API limits rather
 * than omissions.** «Введите почту» has no `email` field anywhere on
 * `v1Employee` to write to, and «Новый пароль» belongs to auth-service, which
 * mints credentials during the attach and exposes no password on this service.
 * Rendering either would collect a value the request silently drops.
 *
 * The EDIT screen additionally drops phone and role: `PATCH .../{id}` accepts
 * `first_name`/`last_name` only. They render as read-only rows so the merchant
 * still sees who they are editing.
 */
export function UserFormSheetMobile({ payload, close }: UserFormSheetMobileProps) {
  const { t } = useTranslation('eats');
  const { open } = useModalControls();

  const isEditing = payload.mode === 'edit';
  const title = t(isEditing ? 'common:modal.edition' : 'common:modal.creation');

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())}>
      <DialogPortal>
        {/* `bg-card`, not the default translucent scrim: this sheet is a
         * full-screen TAKEOVER, so the page behind must be fully covered —
         * the same treatment `product-modal.mobile.tsx` uses. */}
        <DialogOverlay className="bg-card" />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-0 z-50 flex flex-col overflow-hidden bg-card',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          )}
          // Without this Radix focuses the first field on open, which raises
          // the keyboard over a form the merchant hasn't read yet.
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* The nav bar below states the title visibly; Radix still requires
           * these for a11y. */}
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription>

          <MobileNavBar
            className="shrink-0 border-b border-border"
            title={title}
            leading={
              <MobileNavBar.Back onClick={close} aria-label={t('common:actions.back')} />
            }
          />

          {payload.mode === 'edit' ? (
            <EditFields userId={payload.user.id} close={close} />
          ) : (
            <CreateFields
              close={close}
              initialRole={payload.initialRole}
              onCreated={payload.onCreated}
            />
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

/* ------------------------------- Create ------------------------------- */

function CreateFields({
  close,
  initialRole,
  onCreated,
}: {
  close: () => void;
  initialRole?: CreateUserFormValues['role'] | undefined;
  onCreated?: ((role: CreateUserFormValues['role']) => void) | undefined;
}) {
  const { t } = useTranslation('eats');
  const create = useCreateUser();

  const [pickerOpen, setPickerOpen] = useState(false);

  const schema = buildCreateUserFormSchema(t);
  const form = useZodForm(schema, {
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
      role: initialRole ?? 'manager',
    },
    mode: 'onSubmit',
  });

  const role = form.watch('role');

  function handleSubmit(values: CreateUserFormValues): void {
    create.mutate(values, {
      onSuccess: () => {
        toast.success(t('users.form.created'));
        close();
        onCreated?.(values.role);
      },
      // Surface the server's OWN reason — see `lib/attach-errors.ts`.
      onError: (error) => {
        const matched = toCreateUserFieldErrors(getApiFieldErrors(error));
        for (const [field, message] of matched) form.setError(field, { message });
        if (matched.length === 0) {
          form.setError('root', {
            message: getApiErrorMessage(error, t('users.form.errorDescription')),
          });
        }
      },
    });
  }

  return (
    // `min-h-0` on the form and its scroll region both: a flex child's default
    // `min-height:auto` refuses to shrink below its content, which silently
    // defeats every ancestor's overflow.
    <Form
      form={form}
      onSubmit={handleSubmit}
      className="flex min-h-0 flex-1 flex-col gap-0"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        <FormField name="firstName" className={FIELD_GROUP}>
          <FormField.Label className={LABEL}>
            {t('users.form.firstNamePlaceholder')}
          </FormField.Label>
          <FormField.Input className={FIELD} />
          <FormField.Message className={MESSAGE} />
        </FormField>

        <FormField name="lastName" className={FIELD_GROUP}>
          <FormField.Label className={LABEL}>
            {t('users.form.lastNamePlaceholder')}
          </FormField.Label>
          <FormField.Input className={FIELD} />
          <FormField.Message className={MESSAGE} />
        </FormField>

        <FormField name="phone" className={FIELD_GROUP}>
          <FormField.Label className={LABEL}>{t('users.form.phone')}</FormField.Label>
          <FormField.Phone className={FIELD} />
          <FormField.Message className={MESSAGE} />
        </FormField>

        <FormField name="role" className={FIELD_GROUP}>
          <FormField.Label className={LABEL}>{t('users.form.role')}</FormField.Label>
          {/* Not `FormField.Select`: below its breakpoint that degrades to an
           * anchored dropdown, where the design opens a bottom sheet of radio
           * rows with its own confirm (Figma 26295:98832). */}
          <RoleTrigger
            onClick={() => setPickerOpen(true)}
            value={role ? t(`users.roles.${role}`) : undefined}
            placeholder={t('users.form.rolePlaceholder')}
          />
          <FormField.Message className={MESSAGE} />
        </FormField>

        <Form.RootError />
      </div>

      <SubmitBar label={t('common:actions.save')} disabled={create.isPending} />

      {/* Mounted only while open, so each open seeds its local pick from the
       * field rather than an abandoned draft. */}
      {pickerOpen && (
        <MobileRolePicker
          open
          onOpenChange={setPickerOpen}
          title={t('users.form.roleSheetTitle')}
          options={ASSIGNABLE_ROLES.map((value) => ({
            value,
            label: t(roleLabelKey(value)),
          }))}
          value={role}
          onConfirm={(next) =>
            form.setValue('role', next as CreateUserFormValues['role'], {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
          backLabel={t('common:actions.back')}
          confirmLabel={t('users.form.roleConfirm')}
        />
      )}
    </Form>
  );
}

/** The role row: a pill that reads the picked label and opens the sheet. */
function RoleTrigger({
  onClick,
  value,
  placeholder,
}: {
  onClick: () => void;
  value: string | undefined;
  placeholder: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-12 w-full cursor-pointer items-center gap-2 rounded-3xl bg-muted px-4 py-3 text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
    >
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-base tracking-tight',
          value ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {value ?? placeholder}
      </span>
      <ChevronDownIcon className="size-5 shrink-0 text-muted-foreground" />
    </button>
  );
}

/* -------------------------------- Edit -------------------------------- */

function EditFields({ userId, close }: { userId: string; close: () => void }) {
  const { t } = useTranslation('eats');
  const rename = useRenameUser();
  const { removeUser, isPending: isRemoving } = useRemoveUser();

  // Re-read by id on every open, seeded on `dataUpdatedAt` — CLAUDE.md §8's
  // "Edit forms" rule, so reopening never replays a pre-edit copy from cache.
  const { data, dataUpdatedAt } = useUser(userId);

  const schema = buildEditUserFormSchema(t);
  const form = useZodForm(schema, {
    defaultValues: { firstName: '', lastName: '' },
    mode: 'onSubmit',
  });

  const { reset } = form;
  useFormSeed(true, userId, dataUpdatedAt, () => {
    reset({ firstName: data?.firstName ?? '', lastName: data?.lastName ?? '' });
  });

  function handleSubmit(values: EditUserFormValues): void {
    rename.mutate(
      { id: userId, ...values },
      {
        onSuccess: () => {
          toast.success(t('users.form.updated'));
          close();
        },
        onError: (error) =>
          form.setError('root', {
            message: getApiErrorMessage(error, t('users.form.errorDescription')),
          }),
      },
    );
  }

  async function handleRemove(): Promise<void> {
    if (!data) return;
    // Closes only when a delete actually landed — a cancelled confirmation or
    // a failed request leaves the sheet open on the record.
    if (await removeUser(data)) close();
  }

  return (
    <Form
      form={form}
      onSubmit={handleSubmit}
      className="flex min-h-0 flex-1 flex-col gap-0"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        <FormField name="firstName" className={FIELD_GROUP}>
          <FormField.Label className={LABEL}>
            {t('users.form.firstNamePlaceholder')}
          </FormField.Label>
          <FormField.Input className={FIELD} />
          <FormField.Message className={MESSAGE} />
        </FormField>

        <FormField name="lastName" className={FIELD_GROUP}>
          <FormField.Label className={LABEL}>
            {t('users.form.lastNamePlaceholder')}
          </FormField.Label>
          <FormField.Input className={FIELD} />
          <FormField.Message className={MESSAGE} />
        </FormField>

        {/* Disabled: the PATCH body carries the two names and nothing else. */}
        <DisabledField
          label={t('users.form.phone')}
          value={data?.phone ? formatUserPhone(data.phone) : '—'}
        />
        <DisabledField
          label={t('users.form.role')}
          value={data && data.role !== 'unknown' ? t(roleLabelKey(data.role)) : '—'}
        />

        <Form.RootError />

        {/* «Удалить сотрудника» (Figma 26295:98930) — a red text button, not a
         * filled one. It DELETES the employee: the row and its branch
         * assignments go, so there is no branch to wait on. */}
        <button
          type="button"
          onClick={() => void handleRemove()}
          disabled={isRemoving || !data}
          className="mt-2 cursor-pointer self-center text-base font-semibold tracking-tight text-destructive transition-opacity outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40 active:opacity-70 disabled:opacity-50"
        >
          {t('users.form.remove')}
        </button>
      </div>

      <SubmitBar label={t('common:actions.save')} disabled={rename.isPending} />
    </Form>
  );
}

/** A disabled value in the pill's clothing, so the column still reads evenly. */
function DisabledField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-2">
      <span className={LABEL}>{label}</span>
      <input
        className={cn(FIELD, 'text-muted-foreground disabled:cursor-not-allowed')}
        value={value}
        disabled
        readOnly
      />
    </div>
  );
}

/** The pinned footer button band (Figma 26295:98790). */
function SubmitBar({ label, disabled }: { label: string; disabled: boolean }) {
  return (
    <div className="shrink-0 bg-card px-4 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
      <Button
        type="submit"
        size="lg"
        disabled={disabled}
        className="h-14 w-full rounded-full text-base font-semibold"
      >
        {label}
      </Button>
    </div>
  );
}
